<template>
  <div class="blog-constructor">
    <!-- Панель добавления блоков -->
    <div class="add-blocks-panel mb-6 p-4 rounded bg-gray-100 dark:bg-secondary-bg">
      <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-primary">Добавить блок</h3>
      <div class="flex items-center gap-3">
        <button 
          v-for="category in blockCategories" 
          :key="category.id"
          @click="startCreateBlock(category)"
          :title="category.name"
          class="p-3 rounded bg-white dark:bg-background hover:bg-gray-200 dark:hover:bg-secondary-bg-darker border border-gray-300 dark:border-gray-600 hover:border-accent transition-all flex items-center justify-center"
        >
          <img 
            :src="require(`../../assets/icons/${category.icon}`)" 
            :alt="category.name"
            class="w-8 h-8 opacity-80 dark-icon"
          />
        </button>
      </div>
    </div>

    <!-- Область конструктора -->
    <div class="constructor-area">
      <div 
        ref="blocksContainer"
        class="blocks-container space-y-4"
        :class="{ 'empty': blocks.length === 0 }"
      >
        <!-- Пустое состояние -->
        <div v-if="blocks.length === 0" class="empty-state text-center py-12">
          <div class="text-gray-400 text-lg mb-2">Нет блоков</div>
          <div class="text-gray-500 text-sm">Добавьте первый блок, выбрав категорию выше</div>
        </div>

        <!-- Блоки с drag & drop -->
        <div 
          v-for="(block, index) in sortedBlocks" 
          :key="block.id || block._localKey"
          :data-block-id="block.id || block._localKey"
          class="block-wrapper group"
        >
          <!-- Панель управления блоком -->
          <div class="block-controls">
            <div class="block-number">{{ index + 1 }}</div>
            <div class="block-actions">
              <button 
                @click="moveBlockUp(index)"
                :disabled="index === 0"
                class="action-btn"
                title="Переместить выше"
              >
                ↑
              </button>
              <button 
                @click="moveBlockDown(index)"
                :disabled="index === blocks.length - 1"
                class="action-btn"
                title="Переместить ниже"
              >
                ↓
              </button>
              <button 
                @click="editBlock(block)"
                class="action-btn edit"
                title="Редактировать"
              >
                ✎
              </button>
              <button 
                @click="removeBlock(block)"
                class="action-btn delete"
                title="Удалить"
              >
                🗑️
              </button>
            </div>
          </div>

          <!-- Область захвата для перетаскивания -->
          <div class="drag-handle">
            <div class="drag-indicator">
              <div class="drag-dots">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Предварительный просмотр блока -->
          <div class="block-preview">
            <BlockPreview 
              :block="block" 
              :type-options="typeOptions"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно редактирования блока -->
    <div v-if="showForm" class="block-modal" @click.self="cancelBlock">
      <div class="block-modal-content">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-primary">{{ editing ? 'Редактирование блока' : 'Новый блок' }}</h3>
          <button class="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300" @click="cancelBlock">✕</button>
        </div>
        <BlogBlockForm
          :type="editing?.type || ''"
          :position="editing?.position ?? null"
          :value="editing?.payload || {}"
          :mode="editing ? 'edit' : 'create'"
          :initialCategoryId="selectedCategoryId"
          @submit="submitBlock"
          @cancel="cancelBlock"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { useToast } from 'vue-toastification';
import BlogBlockForm from './BlogBlockForm.vue';
import BlockPreview from './BlockPreview.vue';
import { getBlockCategories } from './blockTypes';
import { createBlogBlock, updateBlogBlock, deleteBlogBlock, updateBlogBlockPositions } from '../../services/api';

export default {
  name: 'BlogConstructor',
  components: { 
    BlogBlockForm,
    BlockPreview
  },
  props: {
    articleId: { type: Number, required: true },
    blocks: { type: Array, default: () => [] }
  },
  emits: ['blocks-updated'],
  data() {
    return {
      showForm: false,
      editing: null,
      selectedCategoryId: null,
      blockCategories: getBlockCategories(),
      typeOptions: getBlockCategories().map(cat => ({ value: cat.id, label: cat.name })),
      sortableInstance: null
    }
  },
  computed: {
    sortedBlocks() {
      return [...this.blocks].sort((a, b) => (a.position || 0) - (b.position || 0));
    }
  },
  mounted() {
    this.initSortable();
  },
  beforeUnmount() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
    }
  },
  methods: {
    initSortable() {
      if (!this.$refs.blocksContainer) return;
      
      this.sortableInstance = Sortable.create(this.$refs.blocksContainer, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: this.onSortEnd
      });
    },
    
    onSortEnd(evt) {
      const { oldIndex, newIndex } = evt;
      if (oldIndex === newIndex) return;
      
      this.reorderBlocks(oldIndex, newIndex);
    },
    
    async reorderBlocks(fromIndex, toIndex) {
      const blocks = [...this.sortedBlocks];
      const [movedBlock] = blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, movedBlock);
      
      // Обновляем позиции только для блоков с валидными ID
      const updates = blocks
        .filter(block => block.id && !isNaN(parseInt(block.id))) // Фильтруем только блоки с валидными ID
        .map((block, index) => ({
          ...block,
          position: index + 1
        }));
      
      // Если нет блоков с валидными ID, просто обновляем локальное состояние
      if (updates.length === 0) {
        this.$emit('blocks-updated', blocks);
        return;
      }
      
      try {
        // Отправляем обновления позиций на сервер
        await updateBlogBlockPositions(this.articleId, updates);
        
        // Эмитим событие для обновления родительского компонента
        this.$emit('blocks-updated', blocks);
        
        const toast = useToast();
        toast.success('Порядок блоков обновлен');
      } catch (error) {
        const toast = useToast();
        toast.error('Ошибка при обновлении порядка блоков');
        console.error('Error updating block positions:', error);
        
        // Перезагружаем данные с сервера в случае ошибки
        this.$emit('blocks-updated');
      }
    },
    
    startCreateBlock(category) {
      this.editing = null;
      this.selectedCategoryId = category.id;
      this.showForm = true;
    },
    
    editBlock(block) {
      this.editing = JSON.parse(JSON.stringify(block));
      this.showForm = true;
    },
    
    cancelBlock() {
      this.showForm = false;
      this.editing = null;
      this.selectedCategoryId = null;
    },
    
    async submitBlock(block) {
      try {
        // Преобразуем данные перед отправкой - заменяем _url поля на _file_id
        const transformedBlock = this.transformBlockForSubmission(block);
        
        const wasEditing = this.editing && this.editing.id;
        
        if (wasEditing) {
          await updateBlogBlock(this.articleId, this.editing.id, transformedBlock);
        } else {
          // Определяем следующую позицию
          const maxPosition = Math.max(...this.blocks.map(b => b.position || 0), 0);
          transformedBlock.position = maxPosition + 1;
          
          await createBlogBlock(this.articleId, transformedBlock);
        }
        
        this.showForm = false;
        this.editing = null;
        this.selectedCategoryId = null;
        
        const toast = useToast();
        toast.success(wasEditing ? 'Блок обновлен' : 'Блок добавлен');
        
        // Обновляем список блоков
        this.$emit('blocks-updated');
      } catch (error) {
        const toast = useToast();
        toast.error('Ошибка при сохранении блока');
        console.error('Error saving block:', error);
      }
    },
    
    transformBlockForSubmission(block) {
      // Создаем глубокую копию блока
      const transformedBlock = JSON.parse(JSON.stringify(block));
      
      // Рекурсивно преобразуем все _url поля в _file_id
      this.transformPayload(transformedBlock.payload);
      
      return transformedBlock;
    },
    
    transformPayload(payload) {
      if (!payload || typeof payload !== 'object') return;
      
      for (const key in payload) {
        const value = payload[key];
        
        if (key.endsWith('_url') && value) {
          // Если это URL и он не пустой, оставляем как есть
          // Но если это file_id в виде URL, извлекаем ID
          if (typeof value === 'string' && value.includes('/files/')) {
            const fileIdMatch = value.match(/\/files\/([a-f0-9-]+)/);
            if (fileIdMatch) {
              const fileIdKey = key.replace('_url', '_file_id');
              payload[fileIdKey] = fileIdMatch[1];
              delete payload[key];
            }
          }
        } else if (Array.isArray(value)) {
          // Обрабатываем массивы (галереи)
          value.forEach(item => this.transformPayload(item));
        } else if (value && typeof value === 'object') {
          // Обрабатываем вложенные объекты
          this.transformPayload(value);
        }
      }
    },
    
    async removeBlock(block) {
      if (!block.id) return;
      
      if (confirm('Вы уверены, что хотите удалить этот блок?')) {
        try {
          await deleteBlogBlock(this.articleId, block.id);
          
          const toast = useToast();
          toast.success('Блок удален');
          
          // Обновляем список блоков
          this.$emit('blocks-updated');
        } catch (error) {
          const toast = useToast();
          toast.error('Ошибка при удалении блока');
          console.error('Error deleting block:', error);
        }
      }
    },
    
    moveBlockUp(index) {
      if (index === 0) return;
      this.reorderBlocks(index, index - 1);
    },
    
    moveBlockDown(index) {
      if (index === this.blocks.length - 1) return;
      this.reorderBlocks(index, index + 1);
    }
  }
}
</script>

<style scoped>
.blog-constructor {
  @apply space-y-6;
}

.add-blocks-panel {
  @apply border border-gray-300 dark:border-gray-600;
}

.block-wrapper {
  @apply relative bg-gray-100 dark:bg-secondary-bg rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden;
  @apply transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500;
}

.dark-icon {
  filter: brightness(0) saturate(100%) invert(0);
}

.dark .dark-icon {
  filter: invert(1);
}

.block-controls {
  @apply absolute top-0 left-0 right-0 z-10;
  @apply flex items-center justify-between;
  @apply bg-card/95 border-b border-border backdrop-blur-sm;
  @apply px-3 py-2;
  @apply opacity-0 group-hover:opacity-100 transition-opacity duration-200;
}

.block-number {
  @apply bg-accent text-white text-xs font-bold;
  @apply w-6 h-6 rounded-full flex items-center justify-center;
}

.block-actions {
  @apply flex items-center space-x-1;
  margin-right: 25px;
}

.action-btn {
  @apply w-7 h-7 rounded flex items-center justify-center;
  @apply border border-border bg-muted text-muted-foreground;
  @apply hover:bg-secondary hover:text-foreground;
  @apply transition-colors duration-150;
  @apply text-xs;
}

.action-btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.action-btn.edit {
  @apply text-accent hover:bg-accent hover:text-accent-foreground hover:border-accent;
}

.action-btn.delete {
  @apply text-destructive hover:bg-destructive hover:text-destructive-foreground hover:border-destructive;
}

.drag-handle {
  @apply absolute top-0 right-0 z-20;
  @apply w-8 h-full;
  @apply cursor-grab active:cursor-grabbing;
  @apply flex items-center justify-center;
  @apply bg-muted/80 hover:bg-secondary;
  @apply opacity-0 group-hover:opacity-100 transition-opacity duration-200;
}

.drag-indicator {
  @apply w-4 h-4;
}

.drag-dots {
  @apply grid grid-cols-2 gap-1;
}

.drag-dots span {
  @apply w-1 h-1 bg-muted-foreground rounded-full;
}

.block-preview {
  @apply p-4 pt-12;
}

.empty-state {
  @apply border-2 border-dashed border-gray-600 rounded-lg;
}

/* Sortable styles */
.sortable-ghost {
  @apply opacity-50;
}

.sortable-chosen {
  @apply transform scale-105;
}

.sortable-drag {
  @apply shadow-lg;
}

/* Модальное окно */
.block-modal {
  @apply fixed inset-0 z-50 flex items-center justify-center;
  @apply bg-black bg-opacity-50 dark:bg-opacity-75;
}

.block-modal-content {
  @apply bg-white dark:bg-secondary-bg rounded-lg border border-gray-300 dark:border-gray-600;
  @apply max-w-4xl w-full max-h-[90vh] overflow-y-auto;
  @apply m-4 p-6;
}

/* Адаптивность */
@media (max-width: 768px) {
  .block-controls {
    @apply opacity-100;
  }
  
  .drag-handle {
    @apply opacity-100;
  }
  
  .add-blocks-panel .grid {
    @apply grid-cols-1;
  }
  
  .block-modal-content {
    @apply max-w-full m-2 p-4;
  }
}
</style>
